import type { PortfolioStatementImportV2, StatementImportReviewDecisionV2,
  StatementImportRecordV2, StatementStagedFieldV2 } from "@tradvera/contracts/portfolio-v2";

export type StatementSmartDocumentRoleV2 = "POSITIONS" | "CASH" | "ORDER_HISTORY"
  | "OPEN_ORDERS" | "ACTIVITY_LOG" | "OTHER";

export interface StatementSmartFileSummaryV2 {
  imported: PortfolioStatementImportV2;
  role: StatementSmartDocumentRoleV2;
  actionableRecords: number;
  supportingRecords: number;
  recognizedFields: number;
  exceptionFields: number;
  positionCount: number;
  reportedCash: string | null;
}

export function statementSmartFileSummaryV2(imported: PortfolioStatementImportV2): StatementSmartFileSummaryV2 {
  const records = imported.records;
  const fields = flattenStatementFieldsV2(imported);
  const actionable = records.filter((record) => record.semanticRole !== "DESCRIPTIVE");
  const role = documentRole(records);
  const cashValues = records.filter((record) => record.kind === "CASH_SNAPSHOT")
    .flatMap((record) => record.fields)
    .filter((field) => field.name === "CASH_BALANCE" && field.valueState === "OBSERVED")
    .map((field) => field.normalizedValue ?? field.rawValue).filter((value): value is string => Boolean(value));
  return {
    imported, role, actionableRecords: actionable.length,
    supportingRecords: records.length - actionable.length,
    recognizedFields: fields.filter(isSmartRecognizedFieldV2).length,
    exceptionFields: fields.filter(isSmartExceptionFieldV2).length,
    positionCount: records.filter((record) => record.kind === "POSITION_SNAPSHOT").length,
    reportedCash: cashValues.at(-1) ?? null,
  };
}

export function statementSmartReviewDecisionsV2(imported: PortfolioStatementImportV2): StatementImportReviewDecisionV2[] {
  return flattenStatementFieldsV2(imported).map((field) => ({
    fieldId: field.fieldId,
    decision: isSmartRecognizedFieldV2(field) ? "CONFIRM" : "REJECT",
    correctedValue: null,
  }));
}

/**
 * Projects an owner-approved TradingView bundle into the analytics read model.
 * This never grants ledger authority and never invents broker/account identity:
 * recognized observations become readable evidence, while unknown fields remain
 * explicitly rejected and descriptive rows remain non-actionable.
 */
export function acceptStatementImportForAnalysisV2(
  imported: PortfolioStatementImportV2,
): PortfolioStatementImportV2 {
  const reviewedAt = imported.updatedAt;
  const acceptField = (field: StatementStagedFieldV2): StatementStagedFieldV2 => ({
    ...field,
    reviewState: isSmartRecognizedFieldV2(field) ? "CONFIRMED" : "REJECTED",
    reviewedAt,
  });
  return {
    ...imported,
    state: "OBSERVATIONS_ACCEPTED",
    financialAuthority: "NONE",
    fields: imported.fields.map(acceptField),
    records: imported.records.map((record) => ({
      ...record,
      disposition: record.semanticRole === "ACTIVITY_DELTA" ? "NEW"
        : record.semanticRole === "ABSOLUTE_STATE" ? "SNAPSHOT_ONLY" : "IGNORE",
      fields: record.fields.map(acceptField),
      issueCodes: record.issueCodes.filter((code) => code !== "STATEMENT_IMPORT_REVIEW_REQUIRED"),
    })),
  };
}

export function statementImportPriorityV2(imported: PortfolioStatementImportV2): number {
  const summary = statementSmartFileSummaryV2(imported);
  const role = { POSITIONS: 60, CASH: 50, ORDER_HISTORY: 40, OPEN_ORDERS: 30,
    ACTIVITY_LOG: 20, OTHER: 10 }[summary.role];
  const state = ["OBSERVATIONS_ACCEPTED", "DUPLICATE_NOOP"].includes(imported.state) ? 4
    : imported.state === "ABANDONED" ? -100 : 0;
  return role + state;
}

export function latestStatementBundleV2(imports: readonly PortfolioStatementImportV2[]): PortfolioStatementImportV2[] {
  const accepted = imports.filter((item) => ["OBSERVATIONS_ACCEPTED", "DUPLICATE_NOOP"].includes(item.state));
  const latest = accepted.map((item) => Date.parse(item.createdAt)).filter(Number.isFinite).sort((a, b) => b - a)[0];
  if (latest === undefined) return [];
  const cluster = accepted.filter((item) => {
    const created = Date.parse(item.createdAt);
    return Number.isFinite(created) && latest - created <= 3 * 60_000;
  });
  const byRole = new Map<StatementSmartDocumentRoleV2, PortfolioStatementImportV2>();
  for (const imported of [...cluster].sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))) {
    const role = statementSmartFileSummaryV2(imported).role;
    if (!byRole.has(role)) byRole.set(role, imported);
  }
  return [...byRole.values()].sort((left, right) => statementImportPriorityV2(right) - statementImportPriorityV2(left));
}

export function flattenStatementFieldsV2(imported: PortfolioStatementImportV2): StatementStagedFieldV2[] {
  return [...imported.fields, ...imported.records.flatMap((record) => record.fields)];
}

export function isSmartRecognizedFieldV2(field: StatementStagedFieldV2): boolean {
  return field.name !== "UNCLASSIFIED" && field.valueState === "OBSERVED"
    && field.normalizedValue !== null;
}

export function isSmartExceptionFieldV2(field: StatementStagedFieldV2): boolean {
  return !isSmartRecognizedFieldV2(field)
    || (field.extractor === "OCR" && (field.confidenceBps === null || field.confidenceBps < 9_000));
}

function documentRole(records: readonly StatementImportRecordV2[]): StatementSmartDocumentRoleV2 {
  if (records.some((record) => record.kind === "POSITION_SNAPSHOT")) return "POSITIONS";
  if (records.some((record) => record.kind === "CASH_SNAPSHOT" || record.kind === "CASH_ACTIVITY")) return "CASH";
  if (records.some((record) => record.kind === "TRADE_ACTIVITY" && record.semanticRole === "ACTIVITY_DELTA")) {
    return "ORDER_HISTORY";
  }
  const names = new Set(records.flatMap((record) => record.fields.map((field) => field.name)));
  if (names.has("ORDER_ID") && names.has("STATUS")) return "OPEN_ORDERS";
  if (names.has("DESCRIPTION") && names.has("OCCURRED_AT")) return "ACTIVITY_LOG";
  return "OTHER";
}
